import React, { useEffect, useState } from 'react';
import { PageContainer } from '@urbantxt/layout';
import Loader from 'components/Elements/Loader/Loader';
import { UserSession } from 'types';
import serverErrorHandler from 'services/serverError.service';
import useUserStore from 'store/useUserStore';
import { getSclaAppBackgroundPath } from 'utils/navigation';
import { useNavigate } from 'react-router-dom';
import getSclaApp from '../api/getSclaApp';
import listSclaApplications, {
  SclaApplicationSummary,
} from '../api/listSclaApplications';
import startSclaApp from '../api/startSclaApp';
import useSclaApplicationYears from '../hooks/useSclaApplicationYears';
import SclaNewStudentHome from '../components/home/SclaNewStudentHome';
import SclaPastApplications from '../components/home/SclaPastApplications';
import SclaStartedHome from '../components/home/SclaStartedHome';
import { SclaApplication } from '../types';

export default function SclaHome({ user }: { user: UserSession }) {
  const navigate = useNavigate();
  const storedApplication = useUserStore(state => state.application);
  const { data, error, isLoading } = useSclaApplicationYears();
  const [application, setApplication] = useState<SclaApplication | null>(null);
  const [applicationLoaded, setApplicationLoaded] = useState(false);
  const [pastApplications, setPastApplications] = useState<
    SclaApplicationSummary[]
  >([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const currentYear = data?.years.find(
    year => year.cohortYear === data.currentCohortYear,
  );
  const cohortYear = currentYear?.cohortYear ?? '';

  useEffect(() => {
    if (!cohortYear) return undefined;
    let mounted = true;
    setApplicationLoaded(false);
    getSclaApp(cohortYear)
      .then(app => {
        if (!mounted) return;
        setApplication(app || null);
        if (app) useUserStore.getState().setUserApplication(app);
        setApplicationLoaded(true);
      })
      .catch(loadError => {
        if (!mounted) return;
        setApplicationLoaded(true);
        serverErrorHandler(loadError);
      });
    return () => {
      mounted = false;
    };
  }, [cohortYear]);

  useEffect(() => {
    let mounted = true;
    listSclaApplications()
      .then(applications => {
        if (!mounted) return;
        setPastApplications(applications);
        setListLoaded(true);
      })
      .catch(loadError => {
        if (!mounted) return;
        setListLoaded(true);
        serverErrorHandler(loadError);
      });
    return () => {
      mounted = false;
    };
  }, [user._id]);

  const onStart = async () => {
    if (!cohortYear) return;
    setIsStarting(true);
    try {
      const started = await startSclaApp(cohortYear);
      useUserStore.getState().setUserApplication(started);
      navigate(getSclaAppBackgroundPath());
    } catch (startError) {
      serverErrorHandler(startError);
      setIsStarting(false);
    }
  };

  if (isLoading || (cohortYear && !applicationLoaded) || !listLoaded) {
    return <Loader isVisible fullscreen={false} theme="light" />;
  }

  if (error || !currentYear) {
    return (
      <PageContainer>
        <p>The application calendar could not be loaded.</p>
      </PageContainer>
    );
  }

  let currentApplication: SclaApplication | null = null;
  if (application?.cohortYear === cohortYear) {
    currentApplication = application;
  } else if (storedApplication?.cohortYear === cohortYear) {
    currentApplication = storedApplication;
  }
  const earlierApplications = pastApplications.filter(
    item => item.cohortYear !== cohortYear,
  );
  return (
    <PageContainer>
      {currentApplication ? (
        <SclaStartedHome
          year={currentYear}
          application={currentApplication}
          userType={user.type}
          pastApplications={earlierApplications}
        />
      ) : (
        <>
          <SclaNewStudentHome
            year={currentYear}
            isStarting={isStarting}
            onStart={onStart}
          />
          {currentYear.status === 'closed' || earlierApplications.length > 0 ? (
            <SclaPastApplications applications={earlierApplications} />
          ) : null}
        </>
      )}
    </PageContainer>
  );
}
